import { create } from 'zustand';
import { getFavorites, addFavorite, removeFavorite } from '@/lib/api';
import { consumePendingFavorite } from '@/lib/favoriteIntent';

interface FavoritesState {
  favoriteIds: Set<string>;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadFavorites: () => Promise<void>;
  toggleFavorite: (listingId: string) => Promise<void>;
  completePendingFavorite: () => Promise<void>;
  restoreFavorite: (listingId: string, notes?: string) => Promise<void>;
  isFavorite: (listingId: string) => boolean;
  clearFavorites: () => void;
}

export const useFavoritesStore = create<FavoritesState>((set, get) => ({
  favoriteIds: new Set<string>(),
  isLoading: false,
  error: null,

  /**
   * Load all favorites for the current user
   */
  loadFavorites: async () => {
    set({ isLoading: true, error: null });

    try {
      const favorites = await getFavorites();
      const favoriteIds = new Set(favorites.map((f) => f.listing_id));
      set({ favoriteIds, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load favorites';
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Toggle favorite status for a listing
   */
  toggleFavorite: async (listingId: string) => {
    const { favoriteIds } = get();
    const isFavorited = favoriteIds.has(listingId);

    // Optimistic update
    const newFavoriteIds = new Set(favoriteIds);
    if (isFavorited) {
      newFavoriteIds.delete(listingId);
    } else {
      newFavoriteIds.add(listingId);
    }
    set({ favoriteIds: newFavoriteIds });

    try {
      if (isFavorited) {
        await removeFavorite(listingId);
      } else {
        await addFavorite(listingId);
      }
    } catch (err) {
      // Revert on error
      set({ favoriteIds });
      const message = err instanceof Error ? err.message : 'Failed to update favorite';
      set({ error: message });
      throw err;
    }
  },

  /**
   * Resume an auth-gated favorite after sign-in: a guest tapped the heart,
   * signed in, and landed back here. Add the remembered home (idempotent —
   * skips if it's already saved, so it never clobbers existing notes via the
   * upsert). Runs after loadFavorites so the has-check sees the real set.
   */
  completePendingFavorite: async () => {
    const listingId = consumePendingFavorite();
    if (!listingId) return;

    const { favoriteIds } = get();
    if (favoriteIds.has(listingId)) return; // already saved — nothing to do

    // Optimistic add; the heart across every surface flips to saved.
    const next = new Set(favoriteIds);
    next.add(listingId);
    set({ favoriteIds: next });

    try {
      await addFavorite(listingId);
    } catch {
      // Silent revert — this is a background resume, not a user click, so a
      // failure shouldn't raise a scary banner. The heart simply stays empty.
      set({ favoriteIds });
    }
  },

  /**
   * Re-add a favorite that was just removed (the Undo path), preserving its
   * note. Idempotent + optimistic; reverts on failure.
   */
  restoreFavorite: async (listingId: string, notes?: string) => {
    const { favoriteIds } = get();
    if (favoriteIds.has(listingId)) return;

    const next = new Set(favoriteIds);
    next.add(listingId);
    set({ favoriteIds: next });

    try {
      await addFavorite(listingId, notes);
    } catch (err) {
      set({ favoriteIds });
      const message = err instanceof Error ? err.message : 'Failed to restore favorite';
      set({ error: message });
      throw err;
    }
  },

  /**
   * Check if a listing is favorited
   */
  isFavorite: (listingId: string) => {
    return get().favoriteIds.has(listingId);
  },

  /**
   * Clear favorites (call on logout)
   */
  clearFavorites: () => {
    set({ favoriteIds: new Set<string>(), error: null });
  },
}));
